import React, { useState, useEffect, useRef } from "react";
import ReactGA from "react-ga4";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { toast, ToastContainer } from "react-toastify";
import { Link } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import Buy from "../Subscription/Buypremium";
import GoogleButton from "../../auth/GoogleAuth";
import { analytics } from "../../firebase/firebase";
import { logEvent } from "firebase/analytics";
import { config } from "../../config/config";
import "./Blogs.css";
import Loader from "../../components/Loader";

	const Blogs = () => {
	  const googleAnalyticsId = process.env.REACT_APP_GA_MEASUREMENT_ID;
	  const googleAuthRef = useRef(null);
	  const [blogs, setBlogs]             = useState([]);
	  const [totalBlogs, setTotalBlogs]   = useState(0);
	  const [loading, setLoading]         = useState(true);
	  const [currentPage, setCurrentPage] = useState(1);
	  const blogsPerPage = 6;

	  // initialize Google Analytics once
	  useEffect(() => {
		ReactGA.initialize(googleAnalyticsId);
		ReactGA.send("pageview");
		if (analytics) {
		  logEvent(analytics, "Blogspage_opened_FacelessVideo");
		}
	  }, []);

	  // scroll to top when page changes
	  useEffect(() => {
		window.scrollTo(0, 0);
	  }, [currentPage]);

	  // fetch only the blogs we need for this page
	  useEffect(() => {
		const fetchBlogs = async () => {
		  setLoading(true);
		  try {
			const url = `${config.baseURL}/getBlogs.php?page=${currentPage}&limit=${blogsPerPage}`;
			const res = await fetch(url);
			if (!res.ok) throw new Error("Fetch failed");
			const json = await res.json();
			setBlogs(json.data || []);
			setTotalBlogs(json.total || 0);
			 window.scrollTo(0, 0);
		  } catch (err) {
			console.error(err);
		  } finally {
			setLoading(false);
		  }
		};
		fetchBlogs();
	  }, [currentPage]);

	  const totalPages = Math.ceil(totalBlogs / blogsPerPage);

	  const handlePageChange = (page) => {
		if (page >= 1 && page <= totalPages) {
		  setCurrentPage(page);
		  logEvent(analytics, "blogs_page_changed_FacelessVideo", { page });
		}
	  };

	  return (
		<HelmetProvider>
		  <Helmet>
			<title>
			  Text to Video Blog | Tips, Insights & Trends on AI Video Creation |
			  Generator.Video
			</title>
			<meta
			  name="description"
			  content="Welcome to the Text to Video Blog at Generator.Video. Discover the latest trends, tips, and expert insights on AI-powered text-to-video technology, video creation tips, and marketing strategies."
			/>
			<meta
			  name="keywords"
			  content="Text to Video, AI video creation, video creation blog, video marketing tips, text to video technology, AI video tips, video content creation, Generator.Video, video marketing blog"
			/>
			<link rel="canonical" href="https://generator.video/blog/" />
		  </Helmet>

		  <Header />

		  <div className="Hero_landing-page">
			<h1 className="Hero_title">Text to video Blog</h1>
			<h2 className="Hero-subtitle">
			  Discover the amazing things you'll create with AI
			</h2>
		  </div>

		  <div className="blog-container">
			{loading ? (
			 <Loader show={loading}/>
			) : blogs.length ? (
			  blogs.map((blog) => (
				<div className="blog-card" key={blog.id}>
				  <Link
					to={`/blog/${encodeURIComponent(blog.slug)}/`}
					style={{ textDecoration: "none", color: "inherit" }}
				  >
					<img src={blog.image_url} alt={blog.title} />
					<div className="card-content">
					  <h3>{blog.title}</h3>
					  <p>{blog.summary}</p>
					</div>
				  </Link>
				</div>
			  ))
			) : (
			  <div className="Hero-subtitle">No blogs found.</div>
			)}
		  </div>

		  <div className="pagination_blogspage">
	  <button
		onClick={() => handlePageChange(currentPage - 1)}
		disabled={currentPage === 1}
		rel="prev"
	  >
		Prev
	  </button>

	  {/* Always show first page */}
	  <button
		onClick={() => handlePageChange(1)}
		className={1 === currentPage ? "active" : ""}
	  >
		1
	  </button>

	  {/* Left ellipsis */}
	  {currentPage > 4 && <span className="ellipsis">…</span>}

	  {/* Pages around current */}
	  {Array.from({ length: 5 }, (_, i) => currentPage - 2 + i)
		.filter((p) => p > 1 && p < totalPages)
		.map((page) => (
		  <button
			key={page}
			onClick={() => handlePageChange(page)}
			className={page === currentPage ? "active" : ""}
		  >
			{page}
		  </button>
		))}

	  {/* Right ellipsis */}
	  {currentPage < totalPages - 3 && <span className="ellipsis">…</span>}

	  {/* Always show last page when >1 */}
	  {totalPages > 1 && (
		<button
		  onClick={() => handlePageChange(totalPages)}
		  className={totalPages === currentPage ? "active" : ""}
		  rel="next"
		>
		  {totalPages}
		</button>
	  )}

	  <button
		onClick={() => handlePageChange(currentPage + 1)}
		disabled={currentPage === totalPages}
		rel="next"
	  >
		Next
	  </button>
	</div>

		  <div className="steps_box">
			<h3 className="steps_title" id="HowItWorks">
			  How it Work
			</h3>
			<div className="steps_subtitle">
			  Creating Video is as simple as uploading your content
			</div>
			<div className="all_steps">
			  <div className="step1">
				<img
				  src={config.step1}
				  alt=""
				  className="steps_images"
				/>
				<div className="steps_each_box">
				  <div className="step_number">01</div>
				  <div>
					<div className="steps_text_title">
					  Enter Your Script or Text
					</div>
					<p className="steps_text_detail">
					  Start by uploading your script or typing any text. The Text to
					  video instantly processes your input.
					</p>
				  </div>
				</div>
			  </div>
			  <div className="step2">
				<img
				  src={config.step2}
				  alt=""
				  className="steps_images"
				/>
				<div className="steps_each_box">
				  <div className="step_number">02</div>
				  <div>
					<div className="steps_text_title">
					  Choose a Style & Format
					</div>
					<p className="steps_text_detail">
					  Select from a variety of video styles optimized for Shorts,
					  Reels, TikTok, or explainer videos. The app ensures perfect
					  formatting for each platform.
					</p>
				  </div>
				</div>
			  </div>
			  <div className="step3">
				<img
				  src={config.step3}
				  alt=""
				  className="steps_images"
				/>
				<div className="steps_each_box">
				  <div className="step_number">03</div>
				  <div>
					<div className="steps_text_title">
					  Customize & Preview
					</div>
					<p className="steps_text_detail">
					  Easily edit the video, add subtitles, and tweak colors or
					  transitions to create a polished final product ready for
					  social media.
					</p>
				  </div>
				</div>
			  </div>
			  <div className="step4">
				<img
				  src={config.step4}
				  alt=""
				  className="steps_images"
				/>
				<div className="steps_each_box">
				  <div className="step_number">04</div>
				  <div>
					<div className="steps_text_title">Export & Share</div>
					<p className="steps_text_detail">
					  Download your video in HD and instantly share it on YouTube,
					  Instagram, TikTok, or any social media platform.
					</p>
				  </div>
				</div>
			  </div>
			</div>
		  </div>

		  <Buy showBackButton={false} noMargin={true} />
		  <Footer />
		  <ToastContainer />
		</HelmetProvider>
	  );
	};

	export default Blogs;
